// React-query hooks for the sensor catalog + mappings (issue #4).
//
// useSensorMappings is the app-entry loader: it fetches the tenant's full
// mapping set once (keyed by company so the admin switcher refetches) and
// pushes it into the resolver's module cache, bumping labelsVersion so every
// labeled view re-renders. Mount it high in the tree (AppShell).

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useUserPermissions } from '@/hooks/useMachines';
import {
  listCatalog, listMappings, upsertMapping, deleteMapping,
  type CatalogEntry, type SensorMapping,
} from './api';
import { setCompanyOverrides, clearCompanyOverrides } from '@/sensors/labelResolver';
import { bumpLabelsVersion } from '@/sensors/labelsVersion';
import { setSensorClasses, type SensorClass } from '@/sensors/classRegistry';
import { setFlagDefs } from '@/featureFlags';
import { API_BASE } from '@/apiConfig';

/** Global Sensor Registry (issue #12): ONE fetch loads labels into the
 *  resolver cache AND classes into the class cache. Supersedes the
 *  per-company mapping load — names and behavior are platform-global.
 *  Mount it high in the tree (AppShell). */
export function useSensorRegistry() {
  const token = useAuthStore((s) => s.user?.token ?? null);

  const q = useQuery({
    queryKey: ['sensor-registry'],
    enabled:  !!token,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/sensors/registry`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`registry ${res.status}`);
      return res.json() as Promise<{
        field_name: string; label: string; class: SensorClass; direction: string;
      }[]>;
    },
  });

  useEffect(() => {
    if (q.data) {
      // labels → the existing resolver (global tier: machine null)
      setCompanyOverrides(q.data
        .filter((e) => e.label)
        .map((e) => ({ company_slug: '', machine: null, field_name: e.field_name, display_label: e.label })));
      setSensorClasses(q.data);
      bumpLabelsVersion();
    }
  }, [q.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return q;
}

/** Load the central feature-flag definitions; compiled list is the fallback. */
export function useFlagDefs() {
  const token = useAuthStore((s) => s.user?.token ?? null);
  const q = useQuery({
    queryKey: ['flag-defs'],
    enabled:  !!token,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`flags ${res.status}`);
      return res.json() as Promise<Parameters<typeof setFlagDefs>[0]>;
    },
  });
  useEffect(() => {
    if (q.data) {
      setFlagDefs(q.data);
      bumpLabelsVersion();
    }
  }, [q.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  return q;
}

/** Fetch the tenant's mappings and load them into the resolver cache. Returns
 *  the raw rows too (the /sensors page reads them directly). */
export function useSensorMappings() {
  const token   = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);

  const q = useQuery({
    queryKey: ['sensor-mappings', company],
    enabled:  !!token,
    queryFn:  () => listMappings(token!, company),
  });

  // Push into the module cache whenever the data settles. Keyed on the query's
  // dataUpdatedAt so a refetch (company switch) reloads even if the array
  // reference is reused.
  useEffect(() => {
    if (q.data) {
      setCompanyOverrides(q.data);
      bumpLabelsVersion();
    }
  }, [q.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the cache on logout / company loss so stale names never leak.
  useEffect(() => {
    if (!company) { clearCompanyOverrides(); bumpLabelsVersion(); }
  }, [company]);

  return q;
}

/** Catalog for one machine (external id) or — when omitted — the company-wide
 *  union. Used by the rule field picker + /sensors page. */
export function useSensorCatalog(machineExternalId?: string) {
  const token   = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  return useQuery({
    queryKey: ['sensor-catalog', company, machineExternalId ?? '*'],
    enabled:  !!token,
    queryFn:  () => listCatalog(token!, company, machineExternalId),
  });
}

/** True when the current user may edit sensor names. Admins always can. */
export function useCanManageSensors(): boolean {
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);
  const { data: perms } = useUserPermissions();
  return isAdmin || !!perms?.can_manage_sensors;
}

/** Upsert/delete a mapping with optimistic cache load + labelsVersion bump.
 *  A blank label deletes the tier (revert to inherited). */
export function useMappingMutation() {
  const token   = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  const qc      = useQueryClient();

  return useMutation({
    mutationFn: async (input: { field: string; label: string; machine?: string | null }) => {
      const label = input.label.trim();
      if (label === '') {
        await deleteMapping(token!, { field: input.field, machine: input.machine }, company);
      } else {
        await upsertMapping(token!, { ...input, label }, company);
      }
    },
    onSuccess: async () => {
      // Refetch the authoritative set, reload the cache, bump the version.
      const rows = await qc.fetchQuery<SensorMapping[]>({
        queryKey: ['sensor-mappings', company],
        queryFn:  () => listMappings(token!, company),
      });
      setCompanyOverrides(rows);
      bumpLabelsVersion();
    },
  });
}

export type { CatalogEntry, SensorMapping };
